/**
 * Log levels available for the Logger.
 */
export type ConsoleLogLevel = 'debug' | 'info' | 'warning' | 'error';

/**
 * Log groups determining the active log levels.
 */
export type ConsoleLogGroup = 'DEBUG' | 'INFO' | 'WARNING' | 'PROD';

/**
 * Options for configuring the Logger.
 */
interface LoggerOptions {
  service: string;
  /**
   * Group of log levels to be used by the Logger.
   */
  group?: ConsoleLogGroup;

  /**
   * Flag to hide log values.
   */
  hideValues?: boolean;
}

class Logger {
  private static instance: Logger;
  private service: string;
  private levels: ConsoleLogLevel[];
  private hideValues: boolean;

  /**
   * Creates a Logger instance. Private constructor to enforce singleton pattern.
   * @param {LoggerOptions} options - Options for configuring the Logger.
   */
  private constructor(options: LoggerOptions) {
    this.service = options.service;
    this.levels = this.resolveLogLevels(options.group || 'DEBUG');
    this.hideValues = options.hideValues || false; // Default: false
  }

  /**
   * Gets the singleton instance of the Logger.
   * @param {LoggerOptions} options - Options for configuring the Logger.
   * @returns {Logger} The Logger instance.
   */
  static getInstance(options: LoggerOptions): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger(options);
    }
    return Logger.instance;
  }

  /**
   * Resolves the log levels based on the provided log group.
   * @param {ConsoleLogGroup} group - The log group ('DEBUG', 'INFO', 'WARNING', 'PROD').
   * @returns {ConsoleLogLevel[]} The resolved log levels for the group.
   */
  private resolveLogLevels(group: ConsoleLogGroup): ConsoleLogLevel[] {
    const groups: Record<ConsoleLogGroup, ConsoleLogLevel[]> = {
      DEBUG: ['debug', 'info', 'warning', 'error'],
      INFO: ['info', 'warning', 'error'],
      WARNING: ['info', 'warning', 'error'],
      PROD: ['info', 'error'],
    };

    return groups[group].filter((level): level is ConsoleLogLevel =>
      ['debug', 'info', 'warning', 'error'].includes(level)
    );
  }

  /**
   * Gets the current time in ISO 8601 format.
   * @returns {string} The current timestamp.
   */
  private getCurrentTime(): string {
    const now = new Date();
    return now.toISOString();
  }

  /**
   * Formats the provided value for display in logs.
   * @param {unknown} value - The value to format.
   * @returns {string} The formatted value as a string.
   */
  private formatValue(value: unknown, depth = 0): string {
    if (depth > 3) {
      return ' [Depth Limit Exceeded]';
    }

    if (value instanceof Error) {
      return this.hideValues ? `\nMessage: ${value.message}` : `\nMessage: ${value.message}\nStack: ${value.stack}`;
    }

    if (value && typeof value === 'object') {
      try {
        const formattedObject = Object.entries(value).reduce<Record<string, unknown>>((acc, [key, val]) => {
          acc[key] = this.formatValue(val, depth + 1);
          return acc;
        }, {});
        return `\n${JSON.stringify(formattedObject, null, 2)}`;
      } catch {
        return `\n[Unserializable Object]`;
      }
    }

    return ` ${String(value)}`;
  }

  /**
   * Formats a log message with a timestamp, log level, and optional value.
   * @param {ConsoleLogLevel} type - The log level.
   * @param {string} message - The log message.
   * @param {unknown} [value] - Optional value to include in the log.
   * @returns {string} The formatted log message.
   */
  private formatMessage(type: ConsoleLogLevel, message: string, value?: unknown): string {
    const colors: Record<ConsoleLogLevel, string> = {
      debug: '\x1b[34m', // Blue
      info: '\x1b[36m', // Cyan
      warning: '\x1b[33m', // Yellow
      error: '\x1b[31m', // Red
    };
    const reset = '\x1b[0m';

    const time = this.getCurrentTime();
    const typeLabel = `${colors[type]}[${type.toUpperCase()}]${reset}`;
    const serviceLabel = `[${this.service}]`;
    const valueOutput = !this.hideValues && value !== undefined ? this.formatValue(value) : '';

    return `${time} ${serviceLabel} ${typeLabel} ${message}${valueOutput}`;
  }

  /**
   * Logs a message with the specified log level.
   * @param {ConsoleLogLevel} type - The log level.
   * @param {string} message - The log message.
   * @param {unknown} [value] - Optional value to include in the log.
   */
  log(type: ConsoleLogLevel, message: string, value?: unknown): void {
    if (this.levels.includes(type)) {
      // eslint-disable-next-line no-console
      console.log(this.formatMessage(type, message, value));
    }
  }

  /**
   * Logs a debug-level message.
   * @param {string} message - The log message.
   * @param {unknown} [value] - Optional value to include in the log.
   */
  debug(message: string, value?: unknown): void {
    this.log('debug', message, value);
  }

  /**
   * Logs an info-level message.
   * @param {string} message - The log message.
   * @param {unknown} [value] - Optional value to include in the log.
   */
  info(message: string, value?: unknown): void {
    this.log('info', message, value);
  }

  /**
   * Logs a warning-level message.
   * @param {string} message - The log message.
   * @param {unknown} [value] - Optional value to include in the log.
   */
  warning(message: string, value?: unknown): void {
    this.log('warning', message, value);
  }

  /**
   * Logs an error-level message.
   * @param {string} message - The log message.
   * @param {unknown} [value] - Optional value to include in the log.
   */
  error(message: string, value?: unknown): void {
    this.log('error', message, value);
  }

  /**
   * Sets the log group and updates the active log levels.
   * @param {ConsoleLogGroup} group - The log group to set ('DEBUG', 'INFO', 'PROD').
   */
  setGroup(group: ConsoleLogGroup): void {
    this.levels = this.resolveLogLevels(group);
  }
}

export default Logger;
